import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Create context
const SettingsContext = createContext();

// Default settings
const defaultSettings = {
  language: 'en',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  currency: 'USD',
  notifications: {
    email: true,
    push: true,
    sounds: true
  },
  dashboard: {
    refreshInterval: 5, // minutes
    defaultView: 'charts'
  },
  tables: {
    pageSize: 10,
    densityMode: 'comfortable' // 'comfortable', 'compact', 'spacious'
  },
  sidebar: {
    collapsed: false,
    autoCollapse: true, // auto collapse on mobile
    position: 'left'  // 'left', 'right'
  }
};

// Action types
const SET_SETTING = 'SET_SETTING';
const RESET_SETTINGS = 'RESET_SETTINGS';
const IMPORT_SETTINGS = 'IMPORT_SETTINGS';
const TOGGLE_SIDEBAR = 'TOGGLE_SIDEBAR';

// Settings reducer
function settingsReducer(state, action) {
  switch (action.type) {
    case SET_SETTING:
      // Handle nested properties with dot notation (e.g., "notifications.email")
      if (action.payload.path.includes('.')) {
        const pathParts = action.payload.path.split('.');
        const [parentKey, childKey] = pathParts;
        
        return {
          ...state,
          [parentKey]: {
            ...state[parentKey],
            [childKey]: action.payload.value
          }
        };
      }
      
      // Handle top-level properties
      return {
        ...state,
        [action.payload.path]: action.payload.value
      };
      
    case RESET_SETTINGS:
      return { ...defaultSettings };
      
    case IMPORT_SETTINGS:
      return { ...state, ...action.payload };
      
    case TOGGLE_SIDEBAR:
      return {
        ...state,
        sidebar: {
          ...state.sidebar,
          collapsed: !state.sidebar.collapsed
        }
      };
      
    default:
      return state;
  }
}

// Provider component
export function SettingsProvider({ children }) {
  // Initialize state from localStorage or use defaults
  const [settings, dispatch] = useReducer(settingsReducer, defaultSettings, () => {
    const savedSettings = localStorage.getItem('appSettings');
    return savedSettings ? { ...defaultSettings, ...JSON.parse(savedSettings) } : defaultSettings;
  });
  
  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('appSettings', JSON.stringify(settings));
  }, [settings]);
  
  // Set a specific setting
  const setSetting = (path, value) => {
    dispatch({
      type: SET_SETTING,
      payload: { path, value }
    });
  };
  
  // Reset all settings to defaults
  const resetSettings = () => {
    dispatch({ type: RESET_SETTINGS });
  };
  
  // Import settings (e.g., from a file)
  const importSettings = (newSettings) => {
    dispatch({
      type: IMPORT_SETTINGS,
      payload: newSettings
    });
  };
  
  // Toggle sidebar collapsed state
  const toggleSidebar = () => {
    dispatch({ type: TOGGLE_SIDEBAR });
  };
  
  // Export settings as JSON
  const exportSettings = () => {
    return JSON.stringify(settings, null, 2);
  };
  
  return (
    <SettingsContext.Provider
      value={{
        settings,
        setSetting,
        resetSettings,
        importSettings,
        exportSettings,
        toggleSidebar
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

// Custom hook to use the settings context
export function useSettings() {
  const context = useContext(SettingsContext);
  
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  
  return context;
}